import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, primeCsrf } from './api';
import type { Paginated } from './paginated';
import type { ContactData } from '@/types/api';

export type ContactKind = 'client' | 'vendor' | 'personal' | 'other';

export const KIND_LABELS: Record<string, string> = {
  client: 'Client',
  vendor: 'Vendor',
  personal: 'Personal',
  other: 'Other',
};

export function kindTone(kind: string): 'info' | 'neutral' | 'success' {
  if (kind === 'client') return 'info';
  if (kind === 'personal') return 'success';
  return 'neutral';
}

type DirectoryFilters = {
  kind?: ContactKind;
  q?: string;
  filter?: 'blocked';
  page?: number;
};

/** The whole-business directory: client staff, prospects, vendors, personal. */
export function useContactsDirectory(filters: DirectoryFilters = {}) {
  return useQuery({
    queryKey: ['contacts-directory', filters],
    queryFn: async (): Promise<Paginated<ContactData>> => {
      const { data } = await api.get<Paginated<ContactData>>('/v1/contacts', { params: filters });
      return data;
    },
  });
}

export type IdentifyAction = 'contact' | 'client_contact' | 'lead' | 'client' | 'block';

export type IdentifyPayload = {
  number: string;
  action: IdentifyAction;
  name?: string;
  kind?: 'vendor' | 'personal' | 'other';
  client_id?: number;
  company_name?: string;
  label?: string;
};

function refreshTelephony(qc: ReturnType<typeof useQueryClient>) {
  qc.invalidateQueries({ queryKey: ['messages'] });
  qc.invalidateQueries({ queryKey: ['calls'] });
  qc.invalidateQueries({ queryKey: ['contacts-directory'] });
}

/** Identify an unknown number: create the right record + backfill its history. */
export function useIdentifyNumber() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: IdentifyPayload): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.post<ContactData>('/v1/telephony/identify', payload);
      return data;
    },
    onSuccess: () => refreshTelephony(qc),
  });
}

export type DirectoryContactPayload = {
  name?: string;
  kind?: ContactKind;
  client_id?: number | null;
  notes?: string;
  phones?: { label?: string; number: string; is_primary?: boolean }[];
  emails?: { label?: string; address: string; is_primary?: boolean }[];
};

/** Edit any contact (including a standalone one) from the directory. */
export function useUpdateDirectoryContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      id,
      ...payload
    }: { id: number } & DirectoryContactPayload): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.patch<ContactData>(`/v1/contacts/${id}`, payload);
      return data;
    },
    onSuccess: () => refreshTelephony(qc),
  });
}

/**
 * Remove a contact from the directory. Soft-deletes, so their calls and texts
 * keep their link and the row can be restored; the server refuses a contact who
 * is also a portal user.
 */
export function useDeleteDirectoryContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<void> => {
      await primeCsrf();
      await api.delete(`/v1/contacts/${id}`);
    },
    onSuccess: () => refreshTelephony(qc),
  });
}

export function useBlockContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.post<ContactData>(`/v1/contacts/${id}/block`, {});
      return data;
    },
    onSuccess: () => refreshTelephony(qc),
  });
}

export function useUnblockContact() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: number): Promise<ContactData> => {
      await primeCsrf();
      const { data } = await api.post<ContactData>(`/v1/contacts/${id}/unblock`, {});
      return data;
    },
    onSuccess: () => refreshTelephony(qc),
  });
}
